import React, { useEffect, useState, useContext, createContext } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Loader2, Phone, Mail, MapPin, Check, Settings, X, FileText } from "lucide-react";
import { useAuth } from "../AuthContext";

const BASE = process.env.REACT_APP_BACKEND_URL;

const imgSrc = (u) => (!u ? "" : u.startsWith("http") ? u : `${BASE}${u}`);

const LightboxCtx = createContext(null);
const useLightbox = () => useContext(LightboxCtx);

export function LightboxProvider({ children }) {
  const [url, setUrl] = useState(null);
  return (
    <LightboxCtx.Provider value={setUrl}>
      {children}
      {url && (
        <div className="fixed inset-0 z-[100] bg-black/90 flex items-center justify-center p-3"
          onClick={() => setUrl(null)} data-testid="lightbox">
          <img src={imgSrc(url)} alt="" className="max-w-full max-h-full object-contain" data-testid="lightbox-image" />
          <button type="button" onClick={() => setUrl(null)} data-testid="lightbox-close"
            className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/15 text-white flex items-center justify-center">
            <X className="w-6 h-6" />
          </button>
        </div>
      )}
    </LightboxCtx.Provider>
  );
}

const grad = (a, b) =>
  `linear-gradient(135deg, ${a || "#0f3d2e"} 0%, ${b || "#0a2b20"} 100%)`;

const readable = (bg) => {
  const m = /^#([0-9a-fA-F]{6})$/.exec(bg || "");
  if (!m) return "#111111";
  const n = parseInt(m[1], 16);
  const r = (n >> 16) & 255, g = (n >> 8) & 255, b = n & 255;
  return (0.299 * r + 0.587 * g + 0.114 * b) / 255 > 0.6 ? "#111111" : "#ffffff";
};

const scrollToContact = () => {
  const el = document.querySelector('[data-testid="block-contact"]');
  if (el) el.scrollIntoView({ behavior: "smooth" });
};

function ImgBox({ query, url, accent, className = "" }) {
  const openLb = useLightbox();
  if (url) {
    return (
      <div className="relative overflow-hidden rounded-xl cursor-zoom-in" data-testid="site-img"
        onClick={() => openLb && openLb(url)}>
        <img src={imgSrc(url)} alt={query || ""} className="w-full h-auto block" />
      </div>
    );
  }
  return (
    <div
      className={`relative overflow-hidden rounded-xl ${className}`}
      style={{ background: grad(accent, "#111827") }}
      data-testid="site-img"
    >
      <div className="absolute inset-0 opacity-20"
        style={{ backgroundImage: "radial-gradient(circle at 30% 30%, #fff2 0, transparent 45%)" }} />
      <div className="absolute bottom-2 left-3 text-[10px] uppercase tracking-widest text-white/70">
        {query || ""}
      </div>
    </div>
  );
}

function Section({ children, className = "", style, id }) {
  return (
    <section id={id} className={`px-6 lg:px-10 py-16 lg:py-24 ${className}`} style={style}>
      <div className="max-w-6xl mx-auto">{children}</div>
    </section>
  );
}

function Kicker({ children, accent }) {
  if (!children) return null;
  return (
    <p className="text-xs font-bold uppercase tracking-[0.25em] mb-3" style={{ color: accent }}>
      {children}
    </p>
  );
}

const H2 = ({ children }) =>
  children ? <h2 className="text-3xl lg:text-4xl font-bold tracking-tight mb-10">{children}</h2> : null;

function ContactForm({ slug, brand }) {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [state, setState] = useState("idle"); // idle | sending | done | error
  const [err, setErr] = useState("");
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (form.name.trim().length < 2 || !form.email.trim()) {
      setErr("Completeaza numele si emailul.");
      return;
    }
    setState("sending");
    try {
      await axios.post(`${BASE}/api/public/site/${slug}/contact`, {
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim() || null,
        message: form.message.trim() || null,
      });
      setState("done");
    } catch (ex) {
      setErr(ex?.response?.data?.detail || "Trimiterea a esuat. Reincearca.");
      setState("error");
    }
  };

  if (state === "done") {
    return (
      <div className="bg-slate-50 rounded-xl p-6 border border-slate-200 text-center" data-testid="site-contact-success">
        <Check className="w-8 h-8 mx-auto mb-2" style={{ color: brand }} />
        <p className="font-semibold text-slate-900">Mesaj trimis</p>
        <p className="text-sm text-slate-600 mt-1">Iti multumim. Te contactam cat mai curand.</p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="bg-slate-50 rounded-xl p-6 border border-slate-200 space-y-3" data-testid="site-contact-form">
      <input value={form.name} onChange={set("name")} placeholder="Nume" required
        data-testid="site-contact-name" className="w-full px-4 py-3 rounded-lg border border-slate-200 text-sm" />
      <input value={form.email} onChange={set("email")} type="email" placeholder="Email" required
        data-testid="site-contact-email" className="w-full px-4 py-3 rounded-lg border border-slate-200 text-sm" />
      <input value={form.phone} onChange={set("phone")} placeholder="Telefon"
        data-testid="site-contact-phone" className="w-full px-4 py-3 rounded-lg border border-slate-200 text-sm" />
      <textarea value={form.message} onChange={set("message")} placeholder="Mesaj" rows={4}
        data-testid="site-contact-message" className="w-full px-4 py-3 rounded-lg border border-slate-200 text-sm" />
      {err && <p className="text-sm text-red-600" data-testid="site-contact-error">{err}</p>}
      <button type="submit" disabled={state === "sending"} data-testid="site-contact-submit"
        className="w-full py-3 rounded-lg font-semibold text-white disabled:opacity-60" style={{ background: brand }}>
        {state === "sending" ? "Se trimite..." : "Trimite"}
      </button>
    </form>
  );
}

export const GALLERY_TAB_DEFS = [
  { key: "foto", label: "Foto" },
  { key: "filme", label: "Filme" },
  { key: "certificari", label: "Certificari" },
  { key: "tehnice", label: "Tehnice" },
];

export function normalizeGalleryTabs(d) {
  const src = d.tabs || {};
  const out = [];
  for (const { key, label } of GALLERY_TAB_DEFS) {
    const t = src[key];
    const items = (t && Array.isArray(t.items)) ? t.items : [];
    if (t && t.enabled && items.length) out.push({ key, label, items });
  }
  if (!out.length && Array.isArray(d.images) && d.images.length) {
    out.push({ key: "foto", label: "Foto", items: d.images.map((u) => ({ url: u, kind: "image" })) });
  }
  return out;
}

function GalleryGrid({ items, openLb }) {
  return (
    <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
      {(items || []).map((raw, i) => {
        const it = typeof raw === "string" ? { url: raw, kind: "image" } : raw;
        if (it.kind === "video") {
          return (
            <video key={i} src={imgSrc(it.url)} controls playsInline
              className="w-full rounded-xl border border-slate-200 bg-black self-start" data-testid={`gallery-video-${i}`} />
          );
        }
        if (it.kind === "pdf") {
          return (
            <a key={i} href={imgSrc(it.url)} target="_blank" rel="noreferrer" data-testid={`gallery-pdf-${i}`}
              className="flex items-center gap-3 p-4 rounded-xl border border-slate-200 hover:bg-slate-50 self-start">
              <FileText className="w-6 h-6 shrink-0 text-slate-500" />
              <span className="text-sm font-medium text-slate-800 truncate">{it.name || "Document PDF"}</span>
            </a>
          );
        }
        return (
          <div key={i} onClick={() => openLb && openLb(it.url)} data-testid={`gallery-img-${i}`}
            className="rounded-xl overflow-hidden border border-slate-200 cursor-zoom-in self-start">
            <img src={imgSrc(it.url)} alt="" className="w-full h-auto block" />
          </div>
        );
      })}
    </div>
  );
}

function GalleryBlock({ d, accent }) {
  const openLb = useLightbox();
  const tabs = normalizeGalleryTabs(d);
  const [active, setActive] = useState(tabs[0]?.key || "foto");
  if (!tabs.length) return null;
  const cur = tabs.find((t) => t.key === active) || tabs[0];
  return (
    <Section className="bg-white" data-testid="block-gallery">
      <Kicker accent={accent}>{d.heading || "Galerie"}</Kicker>
      <H2>{d.subheading}</H2>
      {tabs.length > 1 && (
        <div className="flex flex-wrap gap-2 mb-8" data-testid="gallery-tabs">
          {tabs.map((t) => (
            <button key={t.key} type="button" data-testid={`gallery-tab-${t.key}`} onClick={() => setActive(t.key)}
              className="px-5 py-2 rounded-full text-sm font-semibold border transition-colors"
              style={active === t.key
                ? { background: accent, color: readable(accent), borderColor: accent }
                : { background: "transparent", color: "#111111", borderColor: "#e5e7eb" }}>
              {t.label}
            </button>
          ))}
        </div>
      )}
      <GalleryGrid items={cur.items} openLb={openLb} />
    </Section>
  );
}

export function BlockRenderer({ block, theme, slug }) {
  const d = block.data || {};
  const openLb = useLightbox();
  const accent = theme.accent_color || "#111827";
  const primary = theme.primary_color || "#ffffff";
  const onPrimary = readable(primary);
  const brand = readable(primary) === "#ffffff" ? primary : accent;

  switch (block.type) {
    case "hero":
      return (
        <section className="relative px-6 lg:px-10 py-24 lg:py-36 text-center"
          style={{ background: primary, color: onPrimary, borderBottom: onPrimary === "#111111" ? "1px solid #e5e7eb" : "none" }} data-testid="block-hero">
          <div className="max-w-4xl mx-auto">
            {d.tagline && (
              <span className="inline-block text-xs font-semibold uppercase tracking-[0.25em] px-3 py-1 rounded-full mb-6"
                style={{ background: accent, color: readable(accent) }}>{d.badge || d.tagline}</span>
            )}
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-tight">{d.heading}</h1>
            {d.body && <p className="mt-6 text-lg max-w-2xl mx-auto" style={{ color: onPrimary, opacity: 0.8 }}>{d.body}</p>}
            <div className="mt-9 flex flex-wrap gap-3 justify-center">
              {(d.ctas || []).map((c, i) => (
                <button key={i} data-testid={`hero-cta-${i}`}
                  onClick={scrollToContact}
                  className="px-7 py-3 rounded-full font-semibold text-sm transition-transform hover:scale-105"
                  style={i === 0 ? { background: accent, color: readable(accent) }
                    : { background: "transparent", color: onPrimary, border: `1px solid ${onPrimary}55` }}>
                  {c.label}
                </button>
              ))}
            </div>
            {d.image_url && (
              <div className="mt-12 max-w-3xl mx-auto rounded-2xl overflow-hidden shadow-2xl cursor-zoom-in" data-testid="hero-image"
                onClick={() => openLb && openLb(d.image_url)}>
                <img src={imgSrc(d.image_url)} alt={d.heading || ""} className="w-full h-auto block" />
              </div>
            )}
          </div>
        </section>
      );

    case "stats":
      return (
        <Section className="bg-white" data-testid="block-stats">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 text-center">
            {(d.items || []).map((it, i) => (
              <div key={i}>
                <div className="text-4xl font-extrabold" style={{ color: brand }}>{it.value}</div>
                <div className="text-sm text-slate-500 mt-1">{it.label}</div>
              </div>
            ))}
          </div>
        </Section>
      );

    case "services":
    case "benefits":
      return (
        <Section className="bg-slate-50" data-testid={`block-${block.type}`}>
          <Kicker accent={accent}>{d.subheading ? d.heading : null}</Kicker>
          <H2>{d.subheading || d.heading}</H2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {(d.items || []).map((it, i) => (
              <div key={i} className="bg-white rounded-xl p-6 border border-slate-200">
                <div className="w-10 h-1.5 rounded-full mb-4" style={{ background: accent }} />
                <h3 className="font-semibold text-lg text-slate-900">{it.heading}</h3>
                <p className="text-slate-600 text-sm mt-2">{it.body}</p>
              </div>
            ))}
          </div>
        </Section>
      );

    case "cards":
      return (
        <Section className="bg-white" data-testid="block-cards">
          <Kicker accent={accent}>{d.heading}</Kicker>
          <H2>{d.subheading}</H2>
          {d.intro && <p className="text-slate-600 -mt-6 mb-10 max-w-3xl whitespace-pre-line" data-testid="cards-intro">{d.intro}</p>}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {(d.items || []).map((it, i) => (
              <div key={i} className="rounded-xl overflow-hidden border border-slate-200 flex flex-col">
                <ImgBox query={it.image_query} url={it.image_url} accent={accent} className="h-44" />
                <div className="p-5 flex-1 flex flex-col">
                  {it.category && <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">{it.category}</p>}
                  <h3 className="font-semibold text-lg text-slate-900 mt-1">{it.heading}</h3>
                  <p className="text-slate-600 text-sm mt-2 flex-1">{it.body}</p>
                  <div className="flex items-center justify-between mt-4">
                    {it.price && <span className="font-bold" style={{ color: brand }}>{it.price}</span>}
                    {it.cta && <span className="text-sm font-semibold px-4 py-2 rounded-full" style={{ background: accent, color: readable(accent) }}>{it.cta}</span>}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </Section>
      );

    case "steps":
      return (
        <Section className="bg-slate-50" data-testid="block-steps">
          <Kicker accent={accent}>{d.heading}</Kicker>
          <H2>{d.subheading}</H2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {(d.items || []).map((it, i) => (
              <div key={i} className="bg-white rounded-xl p-6 border border-slate-200">
                <div className="text-3xl font-extrabold" style={{ color: accent }}>{it.number || `0${i + 1}`}</div>
                <h3 className="font-semibold text-lg mt-3 text-slate-900">{it.heading}</h3>
                <p className="text-slate-600 text-sm mt-2">{it.description}</p>
              </div>
            ))}
          </div>
        </Section>
      );

    case "projects":
      return (
        <Section className="bg-white" data-testid="block-projects">
          <Kicker accent={accent}>{d.heading}</Kicker>
          <H2>{d.subheading}</H2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {(d.items || []).map((it, i) => (
              <div key={i} className="rounded-xl overflow-hidden border border-slate-200">
                <ImgBox query={it.image_query} url={it.image_url} accent={accent} className="h-52" />
                <div className="p-5">
                  {it.category && <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">{it.category}</p>}
                  <h3 className="font-semibold text-lg text-slate-900 mt-1">{it.heading}</h3>
                  {it.location && <p className="text-sm text-slate-500 flex items-center gap-1 mt-1"><MapPin className="w-3.5 h-3.5" />{it.location}</p>}
                  {it.description && <p className="text-slate-600 text-sm mt-2">{it.description}</p>}
                </div>
              </div>
            ))}
          </div>
        </Section>
      );

    case "case_study":
      return (
        <Section style={{ background: primary === "#ffffff" ? "#0f3d2e" : primary, color: primary === "#ffffff" ? "#ffffff" : onPrimary }} data-testid="block-case_study">
          <H2>{d.heading}</H2>
          {d.description && <p className="text-white/80 -mt-6 mb-10 max-w-2xl">{d.description}</p>}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
            {(d.items || []).map((it, i) => (
              <div key={i} className="rounded-xl p-5" style={{ background: "#ffffff12" }}>
                <div className="text-2xl font-extrabold" style={{ color: accent }}>{it.value}</div>
                <div className="text-sm text-white/70 mt-1">{it.label}</div>
              </div>
            ))}
          </div>
          {d.disclaimer && <p className="text-xs text-white/50 mt-6">{d.disclaimer}</p>}
        </Section>
      );

    case "testimonials":
      return (
        <Section className="bg-slate-50" data-testid="block-testimonials">
          <Kicker accent={accent}>{d.heading}</Kicker>
          <H2>{d.subheading}</H2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {(d.items || []).map((it, i) => (
              <div key={i} className="bg-white rounded-xl p-6 border border-slate-200">
                <p className="text-slate-700 italic">“{it.quote}”</p>
                <p className="text-sm font-semibold text-slate-900 mt-4">{it.author}</p>
              </div>
            ))}
          </div>
        </Section>
      );

    case "clients":
      return (
        <Section className="bg-white" data-testid="block-clients">
          <Kicker accent={accent}>{d.heading}</Kicker>
          <H2>{d.subheading}</H2>
          <div className="flex flex-wrap gap-4">
            {(d.items || []).map((it, i) => (
              <div key={i} className="px-6 py-4 rounded-lg bg-slate-100 text-slate-600 font-semibold text-sm">
                {it.name || it}
              </div>
            ))}
          </div>
        </Section>
      );

    case "faq":
      return (
        <Section className="bg-slate-50" data-testid="block-faq">
          <Kicker accent={accent}>{d.heading}</Kicker>
          <H2>{d.subheading}</H2>
          <div className="space-y-4 max-w-3xl">
            {(d.items || []).map((it, i) => (
              <div key={i} className="bg-white rounded-xl p-5 border border-slate-200">
                <h3 className="font-semibold text-slate-900">{it.question}</h3>
                <p className="text-slate-600 text-sm mt-2">{it.answer}</p>
              </div>
            ))}
          </div>
        </Section>
      );

    case "gallery":
      return <GalleryBlock d={d} accent={accent} />;

    case "cta":
      return (
        <section className="px-6 lg:px-10 py-20 text-center" style={{ background: accent, color: readable(accent) }} data-testid="block-cta">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-3xl lg:text-4xl font-bold" style={{ color: readable(accent) }}>{d.heading}</h2>
            {d.body && <p className="mt-4 text-lg" style={{ color: readable(accent), opacity: 0.85 }}>{d.body}</p>}
            <div className="mt-8 flex gap-3 justify-center">
              {(d.ctas || []).map((c, i) => (
                <button key={i} data-testid={`cta-btn-${i}`} onClick={scrollToContact} className="px-7 py-3 rounded-full font-semibold text-sm" style={{ background: readable(accent), color: accent }}>{c.label}</button>
              ))}
            </div>
          </div>
        </section>
      );

    case "contact":
      return (
        <Section className="bg-white" data-testid="block-contact">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
            <div>
              <H2>{d.heading}</H2>
              {d.body && <p className="text-slate-600 -mt-6 mb-6">{d.body}</p>}
              <div className="space-y-3 text-slate-700">
                {d.phone && <p className="flex items-center gap-2"><Phone className="w-4 h-4" style={{ color: accent }} />{d.phone}</p>}
                {d.email && <p className="flex items-center gap-2"><Mail className="w-4 h-4" style={{ color: accent }} />{d.email}</p>}
                {d.address && <p className="flex items-center gap-2"><MapPin className="w-4 h-4" style={{ color: accent }} />{d.address}</p>}
              </div>
            </div>
            <ContactForm slug={slug} brand={brand} />
          </div>
        </Section>
      );

    case "footer":
      return (
        <footer className="px-6 lg:px-10 py-14" style={{ background: primary, color: onPrimary }} data-testid="block-footer">
          <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-8">
            <div>
              <div className="text-xl font-bold">{d.company}</div>
              {d.tagline && <p className="text-sm mt-2" style={{ color: onPrimary, opacity: 0.7 }}>{d.tagline}</p>}
              {d.body && <p className="text-sm mt-1" style={{ color: onPrimary, opacity: 0.6 }}>{d.body}</p>}
            </div>
            <div className="space-y-2 text-sm" style={{ color: onPrimary, opacity: 0.8 }}>
              {d.phone && <p className="flex items-center gap-2"><Phone className="w-4 h-4" />{d.phone}</p>}
              {d.email && <p className="flex items-center gap-2"><Mail className="w-4 h-4" />{d.email}</p>}
            </div>
            <div className="text-sm md:text-right self-end" style={{ color: onPrimary, opacity: 0.5 }}>{d.copyright}</div>
          </div>
        </footer>
      );

    default:
      return null;
  }
}

export default function PublicSite() {
  const { slug } = useParams();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const isOwnerAdmin = !!user && user.role !== "super_admin" && user.tenant_slug === slug;

  useEffect(() => {
    axios.get(`${BASE}/api/public/site/${slug}`)
      .then(({ data }) => setData(data))
      .catch((e) => setErr(e?.response?.data?.detail || "Site indisponibil"));
  }, [slug]);

  useEffect(() => {
    if (data?.company) document.title = data.company;
  }, [data]);

  if (err) return (
    <div className="min-h-screen flex flex-col items-center justify-center text-slate-500 gap-3" data-testid="public-site-error">
      <div className="text-2xl font-semibold text-slate-700">404</div>
      <p>{err}</p>
      {isOwnerAdmin && (
        <a href="/site" data-testid="public-site-admin-btn"
          style={{ backgroundColor: "#111111", color: "#ffffff", fontFamily: "Arial, sans-serif", fontSize: "13px" }}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-md font-medium">
          <Settings className="w-4 h-4" /> Mergi in administrare
        </a>
      )}
    </div>
  );
  if (!data) return (
    <div className="min-h-screen flex items-center justify-center">
      <Loader2 className="w-6 h-6 animate-spin text-emerald-600" />
    </div>
  );

  return (
    <LightboxProvider>
    <div className="bg-white" data-testid="public-site" style={{ fontFamily: "Arial, Helvetica, sans-serif" }}>
      {isOwnerAdmin && (
        <a href="/site" data-testid="public-site-admin-btn"
          title="Administrare site"
          style={{ position: "fixed", top: 16, right: 16, zIndex: 50, backgroundColor: "#111111", color: "#ffffff", fontFamily: "Arial, sans-serif", fontSize: "13px" }}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-md shadow-lg font-medium">
          <Settings className="w-4 h-4" /> ADMIN
        </a>
      )}
      {(data.blocks || []).map((b, i) => (
        <BlockRenderer key={i} block={b} theme={data.theme || {}} slug={data.slug || slug} />
      ))}
    </div>
    </LightboxProvider>
  );
}
